import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faFilter, 
  faSortAmountDown, 
  faSortAmountUp,
  faShoppingCart,
  faCheck,
  faTimes,
  faChevronDown,
  faCalendarAlt,
  faClock,
  faExclamationTriangle,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import { useCart } from '../../context/CartContext';
import { useLocation } from '../../context/LocationContext';
import PlaceholderImage from '../common/PlaceholderImage';
import LocationGateModal from './LocationGateModal';
import { getAllProductsFilteredByLocation } from '../../services/productService';
import { OFFICE_LOCATIONS, DELIVERY_FEE_CONFIG } from '../../services/distanceService';
import { getCategories } from '../../services/categoryService';
import { validateBookingRequest, getUnavailableDates, validateDateRange, getMinimumBookingDate } from '../../services/availabilityService';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../styles/datepicker-custom.css';
import '../../styles/rentals-page.css';
import '../../styles/ordering-paused.css';

// Add styles for booking notice
const bookingNoticeStyle = `
  .booking-notice {
    background-color: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 4px;
    padding: 8px 12px;
    margin-bottom: 15px;
    display: flex;
    align-items: center;
    gap: 8px;
    color: #856404;
  }
  
  .booking-notice small {
    font-size: 13px;
    margin: 0;
  }
  
  .booking-notice .fa-exclamation-triangle {
    color: #f39c12;
    font-size: 14px;
  }

  /* Location badge + tooltip on rental cards */
  .rental-location {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-top: 6px;
    font-size: 13px;
    color: #555;
    cursor: help;
    outline: none;
  }

  .rental-location .location-label {
    border-bottom: 1px dotted #999;
  }

  .rental-location .location-tooltip {
    position: absolute;
    bottom: 130%;
    left: 0;
    z-index: 20;
    width: 240px;
    background: #2c3e50;
    color: #fff;
    padding: 10px 12px;
    border-radius: 6px;
    font-size: 12px;
    line-height: 1.45;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
    opacity: 0;
    visibility: hidden;
    transform: translateY(4px);
    transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s;
    pointer-events: none;
  }

  .rental-location .location-tooltip::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 16px;
    border-width: 6px;
    border-style: solid;
    border-color: #2c3e50 transparent transparent transparent;
  }

  .rental-location .location-tooltip strong {
    display: block;
    margin-bottom: 3px;
  }

  .rental-location:hover .location-tooltip,
  .rental-location:focus .location-tooltip,
  .rental-location:focus-within .location-tooltip {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
`;

// Pickup/dispatch details per location, shown in the product card tooltip so
// customers know which warehouse a unit ships from and our delivery radius.
const LOCATION_INFO = {
  Angola: { name: 'Angola', address: OFFICE_LOCATIONS.angola.address },
  Elkhart: { name: 'Elkhart', address: OFFICE_LOCATIONS.elkhart.address }
};

const getLocationTooltip = (location) => {
  const info = LOCATION_INFO[location];
  if (info) {
    return {
      label: `${info.name} location`,
      title: `Ships from our ${info.name} location`,
      detail: `${info.address}. We deliver within ${DELIVERY_FEE_CONFIG.maxDeliveryDistance} miles of this location.`
    };
  }
  // "Both" or unknown — available from either warehouse.
  return {
    label: 'Both locations',
    title: 'Available at both locations',
    detail: `Stocked at our Angola and Elkhart locations. We deliver within ${DELIVERY_FEE_CONFIG.maxDeliveryDistance} miles of the serving location.`
  };
};

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = bookingNoticeStyle;
  document.head.appendChild(styleElement);
}

const RentalsPage = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('popular');
  const [priceRange, setPriceRange] = useState([0, 300]);
  const [showFilters, setShowFilters] = useState(false);
  const [filteredRentals, setFilteredRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const { addToCart } = useCart();
  const {
    currentLocation,
    locationConfirmed,
    changeLocation,
    locations,
    isOrderingPaused,
    getOrderingPauseMessage
  } = useLocation();
  const [quickViewItem, setQuickViewItem] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedStartTime, setSelectedStartTime] = useState('');
  const [selectedEndTime, setSelectedEndTime] = useState('');
  const [addedToCart, setAddedToCart] = useState(false);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(null);
  const [dateValidation, setDateValidation] = useState(null);

  // Available time slots for delivery (until 6:00 PM)
  const deliveryTimeSlots = [
    '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', 
    '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM'
  ];

  // Available time slots for pickup (until 8:00 PM)
  const pickupTimeSlots = [
    '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', 
    '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM',
    '6:00 PM', '7:00 PM', '8:00 PM'
  ];

  // Calculate the minimum allowed booking date (24 hours from now)
  const minBookingDate = getMinimumBookingDate();
  
  // Calculate the maximum allowed booking date (12 months from today)
  const maxBookingDate = new Date();
  maxBookingDate.setFullYear(maxBookingDate.getFullYear() + 1);

  // Format dates for display
  const formatDateForDisplay = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleStartDateChange = async (date) => {
    setSelectedStartDate(date);
    setDateValidation(null);
    
    // Validate the date if rental is available (use same date for start and end)
    if (date && quickViewItem?.id) {
      await validateRentalPeriod(date, date);
    }
  };

  const validateRentalPeriod = async (startDate, endDate) => {
    try {
      setAvailabilityLoading(true);
      
      // First validate the date range itself
      const rangeValidation = validateDateRange(startDate, endDate);
      if (!rangeValidation.valid) {
        setAvailabilityError(rangeValidation.message);
        return;
      }
      
      // Then check availability
      const validation = await validateBookingRequest(quickViewItem.id, startDate, endDate);
      setDateValidation(validation);
      
      if (!validation.valid) {
        setAvailabilityError(validation.message);
      } else {
        setAvailabilityError(null);
      }
    } catch (error) {
      console.error('Error validating rental period:', error);
      setAvailabilityError('Unable to verify availability for this period');
    } finally {
      setAvailabilityLoading(false);
    }
  };
  
  const handleStartTimeChange = (e) => {
    setSelectedStartTime(e.target.value);
  };

  const handleEndTimeChange = (e) => {
    setSelectedEndTime(e.target.value);
  };

  const handleQuickView = async (item) => {
    setQuickViewItem(item);
    // Reset date/time selections when opening quick view for a new item
    setSelectedStartDate(null);
    setSelectedStartTime('');
    setSelectedEndTime('');
    setAddedToCart(false);
    setAvailabilityError(null);
    setDateValidation(null);
    document.body.style.overflow = 'hidden';

    // Load unavailable dates for this product
    try {
      setAvailabilityLoading(true);
      const unavailable = await getUnavailableDates(
        item.id, 
        minBookingDate, 
        maxBookingDate
      );
      setUnavailableDates(unavailable);
    } catch (error) {
      console.error('Error loading unavailable dates:', error);
      setAvailabilityError('Unable to load availability information');
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const closeQuickView = () => {
    setQuickViewItem(null);
    setSelectedStartDate(null);
    setSelectedStartTime('');
    setSelectedEndTime('');
    setAddedToCart(false);
    setUnavailableDates([]);
    setAvailabilityError(null);
    setDateValidation(null);
    document.body.style.overflow = 'auto';
  };

  // A unit ships from the warehouse that stocks it. "Both"/unknown units ship
  // from whichever area the customer is browsing, so they follow that switch.
  const itemFulfillmentArea = (item) => {
    const loc = item?.location;
    return loc === 'Angola' || loc === 'Elkhart' ? loc : currentLocation;
  };

  const isItemOrderingPaused = (item) => isOrderingPaused(itemFulfillmentArea(item));

  const handleQuickAdd = async () => {
    // Ordering for this unit's location was paused (possibly while the modal
    // was open) — refuse rather than let a dead booking into the cart.
    if (isItemOrderingPaused(quickViewItem)) {
      alert(getOrderingPauseMessage(itemFulfillmentArea(quickViewItem)));
      return;
    }

    if (!selectedStartDate) {
      alert('Please select an event date for your rental');
      return;
    }

    if (!selectedStartTime) {
      alert('Please select a delivery time');
      return;
    }

    if (!selectedEndTime) {
      alert('Please select a pickup time');
      return;
    }

    // Double-check availability before adding to cart
    try {
      setAvailabilityLoading(true);
      
      // Use the same date for both start and end
      const eventDate = selectedStartDate;
      
      // Validate date range
      const rangeValidation = validateDateRange(eventDate, eventDate);
      if (!rangeValidation.valid) {
        alert(rangeValidation.message);
        return;
      }
      
      // Check availability
      const validation = await validateBookingRequest(quickViewItem.id, eventDate, eventDate);
      
      if (!validation.valid) {
        alert(validation.message);
        return;
      }
      
      // Add to cart with booking info
      const bookingInfo = {
        startDate: eventDate,
        endDate: eventDate,
        startTime: selectedStartTime,
        endTime: selectedEndTime
      };
      
      addToCart(quickViewItem, bookingInfo);
      setAddedToCart(true);
      
      // Close quick view after 1.5 seconds
      setTimeout(() => {
        closeQuickView();
      }, 1500);
    } catch (error) {
      console.error('Error validating booking:', error);
      alert('Unable to verify availability. Please try again.');
    } finally {
      setAvailabilityLoading(false);
    }
  };

  // Filter out unavailable dates in DatePicker
  const isDateUnavailable = (date) => {
    // Convert to local date string to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    const isBlocked = unavailableDates.includes(dateString);
    
    // Debug logging
    if (dateString.includes('2025-06-20') || dateString.includes('2025-06-21')) {
      console.log(`🗓️ Quick view date check: ${dateString} → ${isBlocked ? 'BLOCKED' : 'AVAILABLE'}`);
    }
    
    return isBlocked;
  };

  // Helper function to truncate description for short display
  const getShortDescription = (description) => {
    if (!description) return '';
    
    // Match first sentence (ending with period, exclamation, question mark)
    const match = description.match(/^(.*?[.!?])\s/);
    
    if (match) {
      // Return first sentence
      return match[1];
    } else if (description.length > 100) {
      // If no sentence found, truncate at 100 chars
      return description.substring(0, 100).trim() + '...';
    } else {
      // If description is already short enough, return as is
      return description;
    }
  };

  // Fetch products and categories from Firebase based on current location
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch products filtered by current location
        const productsData = await getAllProductsFilteredByLocation(currentLocation);
        setProducts(productsData);
        
        // Fetch categories from Firebase
        const categoriesData = await getCategories();
        setCategories(categoriesData);
        
        // Convert Firebase products to the format expected by the UI
        const formattedProducts = productsData.map(product => ({
          id: product.id,
          name: product.name,
          price: `$${product.price}/day`,
          priceNumeric: product.price,
          description: product.description,
          shortDescription: product.shortDescription || getShortDescription(product.description), // Use shortDescription if exists, otherwise generate one
          dimensions: product.dimensions || '',
          ageRange: product.ageRange || '',
          capacity: product.capacity || '',
          image: product.images && product.images.length > 0 ? product.images[0].url : '',
          category: product.categoryId,
          popular: product.popular || false,
          location: product.location || currentLocation
        }));
        
        setFilteredRentals(formattedProducts);
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load products. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [currentLocation]); // Re-fetch when location changes
  
  // Update filtered rentals when filters change
  useEffect(() => {
    filterAndSortRentals();
  }, [activeFilter, searchTerm, sortBy, priceRange, products]);

  // Filter and sort products
  const filterAndSortRentals = () => {
    if (!products.length) return;
    
    let filtered = [...products];
    
    // Filter by category
    if (activeFilter !== 'all') {
      // Find category ID that matches the active filter name
      const category = categories.find(cat => cat.name.toLowerCase() === activeFilter.toLowerCase());
      if (category) {
        filtered = filtered.filter(product => product.categoryId === category.id);
      }
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filter by price range - use numeric price value directly from Firebase
    filtered = filtered.filter(product => {
      // Use the numeric price directly from Firebase
      const price = product.price ? parseFloat(product.price) : 0;
      return price >= priceRange[0] && price <= priceRange[1];
    });
    
    // Sort products - use numeric price value directly from Firebase
    switch (sortBy) {
      case 'popular':
        filtered.sort((a, b) => (b.popular === a.popular) ? 0 : b.popular ? 1 : -1);
        break;
      case 'price-low':
        filtered.sort((a, b) => {
          const priceA = a.price ? parseFloat(a.price) : 0;
          const priceB = b.price ? parseFloat(b.price) : 0;
          return priceA - priceB;
        });
        break;
      case 'price-high':
        filtered.sort((a, b) => {
          const priceA = a.price ? parseFloat(a.price) : 0;
          const priceB = b.price ? parseFloat(b.price) : 0;
          return priceB - priceA;
        });
        break;
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default:
        break;
    }
    
    // Convert Firebase products to the format expected by the UI
    const formattedProducts = filtered.map(product => ({
      id: product.id,
      name: product.name,
      price: `$${product.price}/day`,
      priceNumeric: product.price, // Store numeric price for calculations
      description: product.description,
      shortDescription: product.shortDescription || getShortDescription(product.description), // Use shortDescription if exists, otherwise generate one
      dimensions: product.dimensions || '',
      ageRange: product.ageRange || '',
      capacity: product.capacity || '',
      image: product.images && product.images.length > 0 ? product.images[0].url : '',
      category: product.categoryId,
      popular: product.popular || false,
      location: product.location || currentLocation
    }));
    
    setFilteredRentals(formattedProducts);
  };

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };

  const handlePriceRangeChange = (e, index) => {
    const newRange = [...priceRange];
    newRange[index] = parseInt(e.target.value);
    setPriceRange(newRange);
  };

  const clearFilters = () => {
    setActiveFilter('all');
    setSearchTerm('');
    setSortBy('popular');
    setPriceRange([0, 300]);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <div className="rentals-page">
      {!locationConfirmed && (
        <LocationGateModal locations={locations} onConfirm={changeLocation} />
      )}

      <div className="rentals-hero">
        <div className="container">
          <h1>Party Rentals - {currentLocation}</h1>
          <p>Find the perfect rental equipment for your next event in {currentLocation}</p>
        </div>
      </div>

      <div className="container">
        {isOrderingPaused(currentLocation) && (
          <div className="ordering-paused-banner" role="status">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <div>
              <strong>
                We're not taking new online orders for {currentLocation} right now.
              </strong>
              <span>{getOrderingPauseMessage(currentLocation)}</span>
            </div>
          </div>
        )}

        <div className="rentals-toolbar">
          <div className="search-bar">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search rentals..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          
          <div className="toolbar-actions">
            <button className="filter-toggle" onClick={toggleFilters}>
              <FontAwesomeIcon icon={faFilter} />
              Filters
            </button>
            
            <select value={sortBy} onChange={handleSortChange} className="sort-select">
              <option value="popular">Most Popular</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="name-asc">Name: A-Z</option>
              <option value="name-desc">Name: Z-A</option>
            </select>
          </div>
        </div>
        
        {showFilters && (
          <div className="filters-panel">
            <div className="filter-section">
              <h3>Categories</h3>
              <div className="filter-buttons">
                <button 
                  className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
                  onClick={() => handleFilterClick('all')}
                >
                  All Rentals
                </button>
                {categories.map(category => (
                  <button 
                    key={category.id}
                    className={`filter-btn ${activeFilter === category.name ? 'active' : ''}`}
                    onClick={() => handleFilterClick(category.name)}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="filter-section">
              <h3>Price Range</h3>
              <div className="price-range">
                <div className="range-inputs">
                  <input
                    type="range"
                    min="0"
                    max="300"
                    value={priceRange[0]}
                    onChange={(e) => handlePriceRangeChange(e, 0)}
                    className="range-slider"
                  />
                  <input
                    type="range"
                    min="0"
                    max="300"
                    value={priceRange[1]}
                    onChange={(e) => handlePriceRangeChange(e, 1)}
                    className="range-slider"
                  />
                </div>
                <div className="range-values">
                  <span>${priceRange[0]}</span>
                  <span>${priceRange[1]}</span>
                </div>
              </div>
            </div>
            
            <div className="filter-actions">
              <button className="clear-filters" onClick={clearFilters}>
                Clear Filters
              </button>
            </div>
          </div>
        )}
        
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading rentals...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <p>{error}</p>
          </div>
        ) : (
          <>
            <div className="results-count">
              Showing {filteredRentals.length} {filteredRentals.length === 1 ? 'rental' : 'rentals'} in {currentLocation}
            </div>
            
            {filteredRentals.length === 0 ? (
              <div className="no-results">
                <h3>No rentals found</h3>
                <p>Try adjusting your filters or search term, or switch to our other location.</p>
                <button className="clear-filters" onClick={clearFilters}>
                  Clear All Filters
                </button>
              </div>
            ) : (
              <div className="rentals-grid">
                {filteredRentals.map(item => (
                  <div className="rental-card" key={item.id}>
                    <div className="rental-image">
                      {item.image ? (
                        <img src={item.image} alt={item.name} />
                      ) : (
                        <PlaceholderImage alt={item.name} />
                      )}
                      <Link to={`/rentals/${item.id}`} className="view-details">
                        View Details
                      </Link>
                      <button
                        className={`quick-view ${isItemOrderingPaused(item) ? 'ordering-disabled' : ''}`}
                        onClick={() => handleQuickView(item)}
                        disabled={isItemOrderingPaused(item)}
                        title={
                          isItemOrderingPaused(item)
                            ? getOrderingPauseMessage(itemFulfillmentArea(item))
                            : undefined
                        }
                      >
                        {isItemOrderingPaused(item) ? 'Ordering paused' : 'Quick Book'}
                      </button>
                    </div>
                    <div className="rental-info">
                      <h4>{item.name}</h4>
                      <p className="price">{item.price}</p>
                      <p className="short-description">{item.shortDescription}</p>
                      {(() => {
                        const loc = getLocationTooltip(item.location);
                        return (
                          <span className="rental-location" tabIndex={0} aria-label={`${loc.title}. ${loc.detail}`}>
                            <FontAwesomeIcon icon={faMapMarkerAlt} />
                            <span className="location-label">{loc.label}</span>
                            <span className="location-tooltip" role="tooltip">
                              <strong>{loc.title}</strong>
                              {loc.detail}
                            </span>
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Quick View Modal */}
      {quickViewItem && (
        <div className="quick-view-modal">
          <div className="quick-view-backdrop" onClick={closeQuickView}></div>
          <div className="quick-view-content">
            <button className="close-quick-view" onClick={closeQuickView}>×</button>
            
            <div className="quick-view-grid">
              <div className="quick-view-image">
                {quickViewItem.image ? (
                  <img src={quickViewItem.image} alt={quickViewItem.name} />
                ) : (
                  <PlaceholderImage alt={quickViewItem.name} />
                )}
              </div>
              
              <div className="quick-view-info">
                <h2>{quickViewItem.name}</h2>
                <p className="quick-view-price">{quickViewItem.price}</p>
                <p className="quick-view-description">{quickViewItem.description}</p>
                
                <div className="booking-form">
                  {isItemOrderingPaused(quickViewItem) && (
                    <div className="ordering-paused-banner" role="status">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      <div>
                        <strong>
                          Online ordering is paused for our{' '}
                          {itemFulfillmentArea(quickViewItem)} location.
                        </strong>
                        <span>
                          {getOrderingPauseMessage(itemFulfillmentArea(quickViewItem))}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="booking-notice">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <small>Please note: All bookings must be made at least 24 hours in advance.</small>
                  </div>
                  <div className="form-group">
                    <label htmlFor="rental-start-date">
                      <FontAwesomeIcon icon={faCalendarAlt} /> Event Date
                    </label>
                    <DatePicker
                      id="rental-start-date"
                      selected={selectedStartDate}
                      onChange={handleStartDateChange}
                      minDate={minBookingDate}
                      maxDate={maxBookingDate}
                      placeholderText="Choose event date"
                      className="date-picker"
                      dateFormat="MMMM d, yyyy"
                      showPopperArrow={false}
                      popperPlacement="bottom-start"
                      popperModifiers={{
                        flip: {
                          behavior: ["bottom-start", "top-start"]
                        },
                        preventOverflow: {
                          enabled: true,
                          escapeWithReference: false,
                          boundariesElement: "viewport"
                        }
                      }}
                      disabled={availabilityLoading}
                      filterDate={(date) => !isDateUnavailable(date)}
                    />
                    {availabilityLoading && (
                      <div className="availability-loading">
                        <small>Checking availability...</small>
                      </div>
                    )}
                    {availabilityError && (
                      <div className="availability-error">
                        <FontAwesomeIcon icon={faExclamationTriangle} /> {availabilityError}
                      </div>
                    )}
                    {selectedStartDate && !availabilityError && dateValidation?.valid && (
                      <div className="selected-date-confirmation available">
                        <FontAwesomeIcon icon={faCheck} /> Date Available!
                      </div>
                    )}
                    {selectedStartDate && dateValidation && !dateValidation.valid && (
                      <div className="selected-date-confirmation unavailable">
                        <FontAwesomeIcon icon={faExclamationTriangle} /> Date Not Available
                      </div>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="rental-start-time">
                      <FontAwesomeIcon icon={faClock} /> Delivery Time
                    </label>
                    <select
                      id="rental-start-time"
                      value={selectedStartTime}
                      onChange={handleStartTimeChange}
                      className="time-select"
                      disabled={!selectedStartDate || availabilityLoading || (dateValidation && !dateValidation.valid)}
                    >
                      <option value="">Choose delivery time</option>
                      {deliveryTimeSlots.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                    {selectedStartTime && selectedStartDate && dateValidation?.valid && (
                      <div className="selected-time-confirmation">
                        <FontAwesomeIcon icon={faCheck} /> Delivery: {selectedStartTime}
                      </div>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="rental-end-time">
                      <FontAwesomeIcon icon={faClock} /> Pickup Time
                    </label>
                    <select
                      id="rental-end-time"
                      value={selectedEndTime}
                      onChange={handleEndTimeChange}
                      className="time-select"
                      disabled={!selectedStartDate || availabilityLoading || (dateValidation && !dateValidation.valid)}
                    >
                      <option value="">Choose pickup time</option>
                      {pickupTimeSlots.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                    {selectedEndTime && selectedStartDate && dateValidation?.valid && (
                      <div className="selected-time-confirmation">
                        <FontAwesomeIcon icon={faCheck} /> Pickup: {selectedEndTime}
                      </div>
                    )}
                  </div>
                  
                  {unavailableDates.length > 0 && (
                    <div className="availability-info">
                      <p><FontAwesomeIcon icon={faCalendarAlt} /> Some dates are unavailable due to existing bookings.</p>
                    </div>
                  )}
                </div>
                
                <button
                  className={`add-to-cart-button ${addedToCart ? 'added' : ''} ${isItemOrderingPaused(quickViewItem) ? 'ordering-disabled' : ''}`}
                  onClick={handleQuickAdd}
                  disabled={isItemOrderingPaused(quickViewItem) || addedToCart || !selectedStartDate || !selectedStartTime || !selectedEndTime || availabilityLoading || (dateValidation && !dateValidation.valid)}
                >
                  {isItemOrderingPaused(quickViewItem) ? (
                    <>
                      <FontAwesomeIcon icon={faExclamationTriangle} /> Ordering Paused
                    </>
                  ) : availabilityLoading ? (
                    <>
                      <FontAwesomeIcon icon={faClock} /> Checking...
                    </>
                  ) : addedToCart ? (
                    <>
                      <FontAwesomeIcon icon={faCheck} /> Added to Cart!
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faShoppingCart} /> Add to Cart
                    </>
                  )}
                </button>
                
                <Link to={`/rentals/${quickViewItem.id}`} className="view-full-details">
                  View Full Details
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RentalsPage;
